import * as React from 'react';
import classnames from 'classnames';
import { isEmpty, isFunction, snakeCase, uniq } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { __t, applyTranslations } from 'utils/language';
import { expandDataProps, stripPrefix } from 'utils/helper/common';

import './input-array.scss';

applyTranslations(require('utils/i18n/validators.i18n.json'));

import type { Data, TextEntry } from 'types';

type Props = {
  value: Array<string>,
  prefix: string,
  max: number,
  name: string,
  size: 'normal' | 'huge',
  placeholder?: string,
  className?: string,
  disabled?: boolean,
  supportWhiteSpace: boolean,
  hasError?: boolean,
  data?: Data,
  sanitizeFunction?: (value: string) => string,
  onChange: (updatedValues: Array<string>, date?: Data | null) => void
};

class InputArray extends React.PureComponent<Props> {
  static defaultProps = {
    value: [],
    prefix: '',
    max: 3,
    size: 'normal',
    supportWhiteSpace: false
  };

  inputRef: React.RefObject<any> = React.createRef();

  getClassName(baseClass: string, customClass?: string): string {
    const { size, disabled } = this.props;

    return classnames(baseClass, customClass, {
      [`${baseClass}--size-${size}`]: Boolean(size),
      [`${baseClass}--disabled`]: Boolean(disabled)
    });
  }

  filterValues(currentValue: string): Array<string> {
    const { value } = this.props;

    return value.filter(v => v !== currentValue);
  }

  getLabel(item: string): React.ReactNode {
    const { prefix, supportWhiteSpace } = this.props;
    const label = prefix ? `${prefix}${stripPrefix(item)}` : item;

    return (
      <span className="text-overflow-ellipsis">
        {supportWhiteSpace ? label : label.trim()}
      </span>
    );
  }

  handleAdd(inputValue: string): void {
    const trimmedInputValue = inputValue.trim();

    if (isEmpty(trimmedInputValue)) {
      return;
    }

    const { supportWhiteSpace, prefix, value, data, onChange } = this.props;

    const inputItems = trimmedInputValue.split(new RegExp(`([${supportWhiteSpace ? '' : '\\s'},;/])+`, 'g'))
      .map(item => item.replace(/[,;/]/g, '').trim())
      .filter(item => item.length > 0);

    const updatedValues = uniq(
      [...value, ...inputItems].map(item => (prefix ? stripPrefix(item) : item))
    ).map(item => (prefix ? `${prefix}${item}` : item));

    onChange(updatedValues, data);

    this.inputRef.current.value = '';
  }

  handleRemove = (currentValue: string): void => {
    const { data, onChange } = this.props;
    const filteredValues = this.filterValues(currentValue);

    onChange(filteredValues, data);
  };

  handleInputKeyPress = (event: React.KeyboardEvent<TextEntry>): void => {
    const { supportWhiteSpace } = this.props;

    if (event.key === 'Enter' || !supportWhiteSpace && event.key === ' ' || event.key === ',') {
      event.preventDefault();
      event.stopPropagation();

      this.handleInputBlur();
    }
  };

  handleInputKeyDown = (event: React.KeyboardEvent<TextEntry>): void => {
    const { value } = this.props;

    if (event.key === 'Backspace' && !this.inputRef.current.value && value.length > 0) {
      this.handleRemove(value[value.length - 1]);
    }
  };

  handleInputKeyUp = (event: React.KeyboardEvent<TextEntry>): void => {
    const { sanitizeFunction } = this.props;
    const target = event.currentTarget;
    const value = target.value || '';

    if (isFunction(sanitizeFunction)) {
      this.inputRef.current.value = sanitizeFunction(value);
    }
  };

  handleInputBlur = (): void => {
    const currentValue = this.inputRef.current.value;

    this.handleAdd(currentValue);
  };

  handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    this.handleAdd(event.clipboardData.getData('Text'));

    event.preventDefault();
    event.stopPropagation();
  };

  renderError(): React.ReactNode {
    const { value, max, hasError } = this.props;

    const errorClasses = classnames('input-array__error', {
      'input-array__error--visible': Boolean(hasError)
    });

    let message = '';

    if (isEmpty(value)) {
      message = __t('validators.required_field');
    }

    if (value.length > max) {
      message = __t('validators.validation_max_length_array_exceeded', { maxLength: max });
    }

    return (
      <div className={errorClasses}>
        {message}
      </div>
    );
  }

  renderInput(): React.ReactNode {
    const { value, name, data, placeholder, disabled } = this.props;
    const updatedDataProps = {
      ...data,
      qa: `input-array-${name}`
    };

    return (
      <div className={this.getClassName('input-array__field')}>
        <input
          ref={this.inputRef}
          name={name}
          className={this.getClassName('input-array__input')}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          onKeyPress={this.handleInputKeyPress}
          onKeyDown={this.handleInputKeyDown}
          onKeyUp={this.handleInputKeyUp}
          onBlur={this.handleInputBlur}
          onPaste={this.handlePaste}
          {...expandDataProps(updatedDataProps)}
        />
      </div>
    );
  }

  renderSelectedItems = (): Array<JSX.Element> => {
    const { value, size } = this.props;
    const iconSize = size === 'huge' ? 9 : 8;

    return value.map((item, index) => (
      <div
        key={`input-array__${snakeCase(item)}-${index}`}
        className={this.getClassName('input-array__item')}
      >
        {this.getLabel(item)}
        <span
          className={this.getClassName('input-array__item-cross')}
          onClick={() => this.handleRemove(item)}
        >
          <SVGDynamicIcon
            name="cross-custom-fg"
            width={iconSize}
            height={iconSize}
            customColor="primary"
            clickable
          />
        </span>
      </div>
    ));
  };

  render(): React.ReactNode {
    const { value, max, className } = this.props;
    const canAddMore = value.length < max;

    return (
      <div className={this.getClassName('input-array', className)}>
        <div className={this.getClassName('input-array__section')}>
          {this.renderSelectedItems()}
          {
            canAddMore &&
              this.renderInput()
          }
          {this.renderError()}
        </div>
      </div>
    );
  }
}

export default InputArray;
